import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  Grid,
  Paper,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
  Tooltip,
  Button,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Stack,
  alpha,
  Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";

import TopAppBar from "../global/TopBar";
import SideBar from "../global/SideBar";
import { tokens } from "../../theme";
import { useAuth } from "../../AuthContext";

// Icons
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import GroupIcon from "@mui/icons-material/Group";
import InventoryIcon from "@mui/icons-material/Inventory";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PaymentsIcon from "@mui/icons-material/Payments";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isToday = (value) => {
  const d = toDate(value);
  if (!d) return false;
  return d.toDateString() === new Date().toDateString();
};

const pct = (part, total) => (total ? Math.min(100, (part / total) * 100) : 0);

const isTruthyFlag = (v) => v === 1 || v === true || v === "1";

/* ------------------------------------------------------------------ */
/* Presentational sub-components                                       */
/* ------------------------------------------------------------------ */
const SectionCard = ({ ui, title, action, children, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: 3,
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      boxShadow: ui.shadow,
      display: "flex",
      flexDirection: "column",
      ...sx,
    }}
  >
    {(title || action) && (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: ui.textSecondary,
          }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
    )}
    {children}
  </Paper>
);

/* ------------------------------------------------------------------ */
/* Main                                                                */
/* ------------------------------------------------------------------ */
const Admin = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { getData, userRole, userID } = useAuth();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [staffs, setStaffs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({
    totalStaff: 0,
    totalStudents: 0,
    totalParents: 0,
    totalNextOfKin: 0,
    totalInventory: 0,
    totalBorrow: 0,
    totalPayments: 0,
    totalRegistrations: 0,
    totalLeaves: 0,
    pendingLeaves: 0,
    approvedLeaves: 0,
    activeStaff: 0,
    verifiedStaff: 0,
    emailVerified: 0,
    loggedInToday: 0,
    newStaffThisMonth: 0,
  });

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500]; // #0aa125
    const accent2 = colors.newTeal[500]; // #0dcaf0

    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  /* ---------------- Clock ---------------- */
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  /**
   * Every open task deadline, for the whole organisation - this is the admin
   * view, so the point is oversight rather than a personal reminder.
   *
   * Completed tasks are left out: a finished deadline is not a deadline.
   * Overdue outranks priority, because late work is what needs attention.
   */
  const taskEvents = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    const tone = (task, late) => {
      if (late) return { bg: colors.red[500], border: colors.red[600] || colors.red[500] };
      return (
        {
          urgent: { bg: colors.orange[500], border: colors.orange[600] || colors.orange[500] },
          high: { bg: colors.orange[400] || colors.orange[500], border: colors.orange[500] },
          normal: { bg: colors.newTeal[500], border: colors.teal[500] },
          low: { bg: colors.gray[500], border: colors.gray[600] || colors.gray[500] },
        }[task.priority] || { bg: colors.newTeal[500], border: colors.teal[500] }
      );
    };

    return (Array.isArray(tasks) ? tasks : [])
      .filter((t) => t.deadline && String(t.status) !== "completed")
      .map((t) => {
        const deadline = String(t.deadline).slice(0, 10);
        const late = deadline < today;
        const { bg, border } = tone(t, late);

        return {
          id: `task-${t.id}`,
          title: `${t.task_code ? `${t.task_code} · ` : ""}${t.title}`,
          start: deadline,
          allDay: true,
          backgroundColor: bg,
          borderColor: border,
          textColor: "#ffffff",
          extendedProps: { taskId: t.id, overdue: late, priority: t.priority },
        };
      });
  }, [tasks, colors]);

  /** FullCalendar ships its own palette; this pulls it into the theme. */
  const taskCalendarSx = useMemo(
    () => ({
      "& .fc": {
        "--fc-border-color": ui.border,
        "--fc-page-bg-color": "transparent",
        "--fc-today-bg-color": alpha(ui.accent, 0.1),
        "--fc-neutral-bg-color": ui.surfaceAlt,
      },
      "& .fc-toolbar-title": {
        fontSize: "1rem !important",
        fontWeight: "700 !important",
        color: ui.textPrimary,
      },
      "& .fc-button": {
        background: `${ui.surfaceAlt} !important`,
        border: `1px solid ${ui.border} !important`,
        borderRadius: "8px !important",
        color: `${ui.textSecondary} !important`,
        fontSize: "0.72rem !important",
        fontWeight: "600 !important",
        textTransform: "capitalize !important",
        boxShadow: "none !important",
        "&:hover": {
          background: `${alpha(ui.accent, 0.15)} !important`,
          color: `${ui.textPrimary} !important`,
        },
      },
      "& .fc-button-active": {
        background: `${alpha(ui.accent, 0.25)} !important`,
        color: `${ui.textPrimary} !important`,
      },
      "& .fc-col-header-cell-cushion": {
        color: ui.textSecondary,
        fontSize: "0.68rem",
        fontWeight: 700,
        textTransform: "uppercase",
        textDecoration: "none !important",
      },
      "& .fc-daygrid-day-number": {
        color: ui.textSecondary,
        fontSize: "0.75rem",
        textDecoration: "none !important",
      },
      "& .fc-event": {
        borderRadius: "6px !important",
        padding: "1px 5px !important",
        fontSize: "0.7rem !important",
        fontWeight: "600 !important",
        cursor: "pointer",
      },
      "& .fc-list-event-title a": {
        color: `${ui.textPrimary} !important`,
        textDecoration: "none !important",
      },
      "& .fc-list-day-cushion": { background: `${ui.surfaceAlt} !important` },
      "& .fc-list-day-text, & .fc-list-day-side-text": {
        color: ui.textPrimary,
        textDecoration: "none !important",
      },
    }),
    [ui]
  );

  /* ---------------- Data ---------------- */
  const fetchDashboardData = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) setRefreshing(true);

      const endpoints = [
        { name: "staffs", key: "totalStaff" },
        { name: "students_V2", key: "totalStudents" },
        { name: "parent_V2", key: "totalParents" },
        { name: "next_of_keen_V2", key: "totalNextOfKin" },
        { name: "inventory", key: "totalInventory" },
        { name: "borrow_V2", key: "totalBorrow" },
        { name: "payment_V2", key: "totalPayments" },
        { name: "registration_V2", key: "totalRegistrations" },
        { name: "leaves_V2", key: "totalLeaves" },
        { name: "tasks_V2", key: "totalTasks" },
      ];

      try {
        // Fire all requests in parallel instead of sequentially.
        const results = await Promise.all(
          endpoints.map(async (endpoint) => {
            try {
              const response = await getData("proccess.php", endpoint.name);
              return { endpoint, data: response?.success ? response.data : null };
            } catch (err) {
              console.error(`Error fetching ${endpoint.name}:`, err);
              return { endpoint, data: null };
            }
          })
        );

        const next = {};
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        results.forEach(({ endpoint, data }) => {
          if (!Array.isArray(data)) return;
          next[endpoint.key] = data.length;

          if (endpoint.name === "leaves_V2") {
            next.pendingLeaves = data.filter((l) => l.status === 0).length;
            next.approvedLeaves = data.filter((l) => l.status === 4).length;
          }

          if (endpoint.name === "tasks_V2") {
            setTasks(data);
          }

          if (endpoint.name === "staffs") {
            setStaffs(data);
            next.activeStaff = data.filter(
              (s) => s.status === "active" || s.status === 1
            ).length;
            next.verifiedStaff = data.filter((s) => isTruthyFlag(s.isVerified)).length;
            next.emailVerified = data.filter((s) =>
              isTruthyFlag(s.email_verified)
            ).length;
            next.newStaffThisMonth = data.filter((s) => {
              const d = toDate(s.createDate);
              return d && d >= firstDayOfMonth;
            }).length;
            next.loggedInToday = data.filter((s) => isToday(s.last_login)).length;

            const me = data.find((u) => u.Id === userID);
            if (me) setCurrentUser(me);
          }
        });

        setStats((prev) => ({ ...prev, ...next }));
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
        if (showRefresh) setTimeout(() => setRefreshing(false), 800);
      }
    },
    [getData, userID]
  );

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /* ---------------- Redirect receptionist ---------------- */
  useEffect(() => {
    if (userRole === "receptionist") navigate("/website/dashboard");
  }, [userRole, navigate]);

  /* ---------------- Derived view data ---------------- */
  const formattedDate = currentTime.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const onlineUsers = useMemo(
    () => staffs.filter((s) => isToday(s.last_login)),
    [staffs]
  );

  const roleColor = useCallback(
    (role) =>
      ({
        admin: colors.red[500],
        hr: colors.newTeal[500],
        supervisor: colors.green[500],
        receptionist: colors.orange[500],
      }[role] || colors.indigo[500]),
    [colors]
  );

  const statCards = useMemo(
    () => [
      {
        title: "Total Staff",
        value: stats.totalStaff,
        icon: <PeopleIcon />,
        color: colors.teal[500],
        // Only this card has a real, computed delta.
        delta: stats.newStaffThisMonth,
        deltaLabel: "this month",
        path: "/admin/dashboard/staffs",
      },
      {
        title: "Total Students",
        value: stats.totalStudents,
        icon: <SchoolIcon />,
        color: colors.green[500],
        path: "/admin/dashboard/students",
      },
      {
        title: "Parents & Guardians",
        value: stats.totalParents,
        icon: <FamilyRestroomIcon />,
        color: colors.indigo[500],
        path: "/admin/dashboard/parentsguardians",
      },
      {
        title: "Next of Kin",
        value: stats.totalNextOfKin,
        icon: <GroupIcon />,
        color: colors.orange[500],
        path: "/admin/dashboard/nextofkin",
      },
      {
        title: "Inventory",
        value: stats.totalInventory,
        icon: <InventoryIcon />,
        color: colors.newTeal[500],
        path: "/admin/dashboard/inventory",
      },
      {
        title: "Borrow Records",
        value: stats.totalBorrow,
        icon: <AssignmentIcon />,
        color: colors.red[500],
        path: "/admin/dashboard/borrow",
      },
      {
        title: "Payments",
        value: stats.totalPayments,
        icon: <PaymentsIcon />,
        color: colors.yellow[500],
        path: "/admin/dashboard/payments",
      },
      {
        title: "Registrations",
        value: stats.totalRegistrations,
        icon: <HowToRegIcon />,
        color: colors.blue[500],
        path: "/admin/dashboard/registrations",
      },
    ],
    [stats, colors]
  );

  const avatarSrc = (staff) =>
    staff.image
      ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}`
      : "";

  const initials = (staff) =>
    `${staff.FirstName?.[0] || ""}${staff.LastName?.[0] || ""}`.toUpperCase();

  /* ------------------------------------------------------------------ */
  return (
    <Box sx={{ display: "flex", height: "100vh", bgcolor: ui.pageBg }}>
      <SideBar />

      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <TopAppBar />

        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Container maxWidth="xl" sx={{ py: 3.5 }}>
            {/* ---------------------------------------------------------- */}
            {/* Page header                                                 */}
            {/* ---------------------------------------------------------- */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              sx={{ mb: 3.5 }}
            >
              <Box>
                <Typography
                  variant="h2"
                  sx={{ fontWeight: 800, letterSpacing: -0.4, color: ui.textPrimary }}
                >
                  Admin Dashboard
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
                  Organisation overview and activity at a glance
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.15,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: ui.surface,
                    border: `1px solid ${ui.border}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarTodayIcon sx={{ color: ui.accent, fontSize: 17 }} />
                    <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                      {formattedDate}
                    </Typography>
                  </Stack>

                  <Divider orientation="vertical" flexItem sx={{ borderColor: ui.border }} />

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTimeIcon sx={{ color: colors.orange[500], fontSize: 17 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        color: colors.orange[500],
                      }}
                    >
                      {formattedTime}
                    </Typography>
                  </Stack>
                </Paper>

                <Tooltip title="Refresh data">
                  <IconButton
                    onClick={() => fetchDashboardData(true)}
                    disabled={refreshing}
                    sx={{
                      borderRadius: 2,
                      color: ui.textSecondary,
                      bgcolor: ui.surface,
                      border: `1px solid ${ui.border}`,
                      "&:hover": {
                        color: ui.accent,
                        borderColor: alpha(ui.accent, 0.5),
                        bgcolor: alpha(ui.accent, 0.08),
                      },
                      "@keyframes spin": {
                        from: { transform: "rotate(0deg)" },
                        to: { transform: "rotate(360deg)" },
                      },
                    }}
                  >
                    <RefreshIcon
                      sx={{
                        fontSize: 20,
                        animation: refreshing ? "spin 1s linear infinite" : "none",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {/* ---------------------------------------------------------- */}
            {/* Welcome banner                                              */}
            {/* ---------------------------------------------------------- */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                mb: 4,
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                background: ui.gradient,
                boxShadow: `0 18px 40px -24px ${alpha(ui.accent, 0.9)}`,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: -120,
                  right: -60,
                  width: 340,
                  height: 340,
                  borderRadius: "50%",
                  background: alpha("#ffffff", 0.1),
                },
              }}
            >
              <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 800, color: "#ffffff", mb: 1 }}
                  >
                    Welcome back, {currentUser?.FirstName || "Admin"}
                  </Typography>
                  <Typography variant="h5" sx={{ color: alpha("#ffffff", 0.9), lineHeight: 1.6 }}>
                    Here's what's happening in your organisation today.
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      p: 2.25,
                      borderRadius: 2.5,
                      bgcolor: alpha("#ffffff", 0.14),
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${alpha("#ffffff", 0.22)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1.25,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        color: alpha("#ffffff", 0.8),
                      }}
                    >
                      Active staff
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: "#ffffff" }}>
                        {stats.activeStaff}
                        <Typography
                          component="span"
                          variant="h6"
                          sx={{ color: alpha("#ffffff", 0.75), ml: 0.5 }}
                        >
                          / {stats.totalStaff}
                        </Typography>
                      </Typography>
                      <Typography variant="h6" sx={{ color: alpha("#ffffff", 0.85), alignSelf: "flex-end" }}>
                        {Math.round(pct(stats.activeStaff, stats.totalStaff))}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={pct(stats.activeStaff, stats.totalStaff)}
                      sx={{
                        height: 7,
                        borderRadius: 999,
                        bgcolor: alpha("#ffffff", 0.25),
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#ffffff",
                          borderRadius: 999,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* ---------------------------------------------------------- */}
            {/* Stat cards on the left, task deadlines on the right          */}
            {/* ---------------------------------------------------------- */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, lg: 7 }}>
                {/* Two per row inside the narrower column, four only when the
                    calendar is not beside it. */}
                <Grid container spacing={2.5}>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={`sk-${i}`}>
                      <Skeleton
                        variant="rounded"
                        height={158}
                        sx={{ borderRadius: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
                      />
                    </Grid>
                  ))
                : statCards.map((card) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={card.title}>
                      <Card
                        onClick={() => navigate(card.path)}
                        elevation={0}
                        sx={{
                          height: "100%",
                          borderRadius: 3,
                          cursor: "pointer",
                          position: "relative",
                          overflow: "hidden",
                          bgcolor: ui.surface,
                          border: `1px solid ${ui.border}`,
                          transition: "transform .2s ease, border-color .2s ease, box-shadow .2s ease",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            insetInlineStart: 0,
                            top: 0,
                            bottom: 0,
                            width: 3,
                            bgcolor: card.color,
                            opacity: 0,
                            transition: "opacity .2s ease",
                          },
                          "&:hover": {
                            transform: "translateY(-4px)",
                            borderColor: alpha(card.color, 0.5),
                            boxShadow: `0 16px 32px -18px ${alpha(card.color, 0.8)}`,
                            "&::before": { opacity: 1 },
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                          >
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                display: "grid",
                                placeItems: "center",
                                borderRadius: 2,
                                color: card.color,
                                bgcolor: alpha(card.color, isDark ? 0.16 : 0.12),
                              }}
                            >
                              {card.icon}
                            </Box>

                            {card.delta > 0 && (
                              <Chip
                                size="small"
                                icon={<TrendingUpIcon />}
                                label={`+${card.delta} ${card.deltaLabel}`}
                                sx={{
                                  height: 24,
                                  fontWeight: 700,
                                  fontSize: "0.68rem",
                                  color: colors.green[500],
                                  bgcolor: alpha(colors.green[500], 0.12),
                                  "& .MuiChip-icon": { color: "inherit", fontSize: 15 },
                                }}
                              />
                            )}
                          </Stack>

                          <Typography
                            variant="h2"
                            sx={{
                              fontWeight: 800,
                              lineHeight: 1.1,
                              fontVariantNumeric: "tabular-nums",
                              color: ui.textPrimary,
                            }}
                          >
                            {(card.value ?? 0).toLocaleString()}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              mt: 0.75,
                              letterSpacing: 0.5,
                              textTransform: "uppercase",
                              color: ui.textSecondary,
                            }}
                          >
                            {card.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, lg: 5 }}>
              <SectionCard
                ui={ui}
                title="Task Calendar"
                action={
                  <Button
                    size="small"
                    endIcon={<ArrowForwardRoundedIcon />}
                    onClick={() => navigate("/tasks/dashboard")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: ui.accent,
                      "&:hover": { bgcolor: alpha(ui.accent, 0.1) },
                    }}
                  >
                    Open board
                  </Button>
                }
              >
                {taskEvents.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <AssignmentIcon
                      sx={{ fontSize: 40, color: alpha(ui.textSecondary, 0.5) }}
                    />
                    <Typography variant="h6" sx={{ color: ui.textSecondary, mt: 1 }}>
                      No task deadlines to show
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={taskCalendarSx}>
                    <FullCalendar
                      plugins={[dayGridPlugin, listPlugin]}
                      initialView="dayGridMonth"
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,listMonth",
                      }}
                      events={taskEvents}
                      height={430}
                      eventDisplay="block"
                      dayMaxEvents={3}
                      // A deadline is a date, not an appointment.
                      displayEventTime={false}
                      eventClick={(info) => {
                        info.jsEvent.preventDefault();
                        navigate(`/tasks/${info.event.extendedProps.taskId}`);
                      }}
                    />
                  </Box>
                )}
              </SectionCard>
              </Grid>
            </Grid>

            {/* ---------------------------------------------------------- */}
            {/* Activity row                                                */}
            {/* ---------------------------------------------------------- */}
            <Grid container spacing={2.5}>
              {/* Recent staff */}
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                  ui={ui}
                  title="Recent Staff Members"
                  action={
                    <Button
                      size="small"
                      endIcon={<ArrowForwardRoundedIcon />}
                      onClick={() => navigate("/admin/dashboard/staffs")}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        color: ui.accent,
                        "&:hover": { bgcolor: alpha(ui.accent, 0.1) },
                      }}
                    >
                      View all
                    </Button>
                  }
                >
                  <List disablePadding>
                    {staffs.slice(0, 5).map((staff, index, arr) => (
                      <Box key={staff.Id}>
                        <ListItem
                          disableGutters
                          sx={{
                            px: 1,
                            py: 1.25,
                            borderRadius: 2,
                            transition: "background-color .18s ease",
                            "&:hover": { bgcolor: alpha(ui.accent, 0.06) },
                          }}
                          secondaryAction={
                            <Tooltip title="View profile">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/admin/dashboard/staff/${staff.Id}`)}
                                sx={{
                                  color: ui.textSecondary,
                                  "&:hover": { color: ui.accent, bgcolor: alpha(ui.accent, 0.1) },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={avatarSrc(staff)}
                              sx={{
                                width: 44,
                                height: 44,
                                fontWeight: 700,
                                bgcolor: ui.surfaceAlt,
                                color: ui.textPrimary,
                                border: `2px solid ${
                                  staff.status === "active" || staff.status === 1
                                    ? colors.green[500]
                                    : alpha(ui.textSecondary, 0.4)
                                }`,
                              }}
                            >
                              {initials(staff)}
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            disableTypography
                            primary={
                              <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: ui.textPrimary }}
                              >
                                {staff.FirstName} {staff.LastName}
                              </Typography>
                            }
                            secondary={
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                <Chip
                                  label={staff.role || "Staff"}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    textTransform: "capitalize",
                                    color: roleColor(staff.role),
                                    bgcolor: alpha(roleColor(staff.role), 0.12),
                                  }}
                                />
                                <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                                  {staff.department || "No department"}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItem>
                        {index < arr.length - 1 && (
                          <Divider sx={{ borderColor: ui.border }} />
                        )}
                      </Box>
                    ))}
                  </List>
                </SectionCard>
              </Grid>

              {/* Online staff */}
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                  ui={ui}
                  title="Online Staff"
                  action={
                    <Chip
                      size="small"
                      label={`${onlineUsers.length} online`}
                      sx={{
                        fontWeight: 700,
                        color: colors.green[500],
                        bgcolor: alpha(colors.green[500], 0.12),
                      }}
                    />
                  }
                >
                  {onlineUsers.length > 0 ? (
                    <List disablePadding>
                      {onlineUsers.slice(0, 5).map((staff, index, arr) => (
                        <Box key={staff.Id}>
                          <ListItem disableGutters sx={{ px: 1, py: 1.25 }}>
                            <ListItemAvatar>
                              <Box sx={{ position: "relative" }}>
                                <Avatar
                                  src={avatarSrc(staff)}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    fontWeight: 700,
                                    bgcolor: ui.surfaceAlt,
                                    color: ui.textPrimary,
                                  }}
                                >
                                  {initials(staff)}
                                </Avatar>
                                <Box
                                  sx={{
                                    position: "absolute",
                                    right: 8,
                                    bottom: 0,
                                    width: 11,
                                    height: 11,
                                    borderRadius: "50%",
                                    bgcolor: colors.green[500],
                                    border: `2px solid ${ui.surface}`,
                                  }}
                                />
                              </Box>
                            </ListItemAvatar>

                            <ListItemText
                              disableTypography
                              primary={
                                <Typography
                                  variant="h5"
                                  sx={{ fontWeight: 700, color: ui.textPrimary }}
                                >
                                  {staff.FirstName} {staff.LastName}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="h6"
                                  sx={{ mt: 0.4, color: ui.textSecondary }}
                                >
                                  <Box component="span" sx={{ color: colors.green[500], fontWeight: 700 }}>
                                    Online
                                  </Box>
                                  {" • last active "}
                                  {toDate(staff.last_login)?.toLocaleTimeString("en-US", {
                                    hour12: false,
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }) || "unknown"}
                                </Typography>
                              }
                            />
                          </ListItem>
                          {index < arr.slice(0, 5).length - 1 && (
                            <Divider sx={{ borderColor: ui.border }} />
                          )}
                        </Box>
                      ))}
                    </List>
                  ) : (
                    <Stack alignItems="center" spacing={1.25} sx={{ py: 6 }}>
                      <PersonOffOutlinedIcon sx={{ fontSize: 38, color: ui.textSecondary, opacity: 0.6 }} />
                      <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                        No staff online at the moment
                      </Typography>
                    </Stack>
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Admin;